import { useCallback, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import grayStar from '~/images/grayStar.png';
import greenStar from '~/images/star_Green.png';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import '~/themes/ratingInput.scss';
import { Box } from '../Box';
import { Row } from '../Row';

export type TFlexDirection = 'row' | 'column';

export interface IRatingInputProps {
  containerStyle?: CSSProperties;
  direction?: TFlexDirection;
  errorMessage?: string;
  isError?: boolean;
  maxValue?: number;
  title?: string;
  value?: number;
  onChange?: (rating: number) => void;
}

/**
 * RatingInput Component
 *
 * A customizable star rating input component with configurable star count, layout direction,
 * and error handling. Provides visual feedback for user ratings with selectable stars.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for job rating functionality in JobSummaryRating.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/JobSummary/components/RatingInput
 * - src/components/Rating/RatingInput
 *
 * If this is truly only used for job rating contexts, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <RatingInput
 *   title="Professional Skills"
 *   value={skillsRating}
 *   onChange={setSkillsRating}
 *   maxValue={5}
 *   isError={!skillsRating}
 *   errorMessage="Please provide a rating"
 * />
 * ```
 */
export const RatingInput = ({
  title,
  value = 0,
  maxValue = 5,
  isError = false,
  errorMessage = 'Please enter a value.',
  containerStyle,
  direction = 'column',
  onChange = () => {},
}: IRatingInputProps) => {
  const isColumnDirection = direction === 'column';
  const isRowDirection = direction === 'row';

  // Generate a data-cy prefix based on title (e.g., "Professional Skills" -> "professional-skills")
  const dataCyPrefix = title ? title.toLowerCase().replace(/\s+/g, '-') : 'rating';

  const handleStarClick = useCallback(
    (rating: number) => (event: ReactMouseEvent<HTMLImageElement>) => {
      event.preventDefault();
      onChange(rating);
    },
    [onChange],
  );

  return (
    <Box
      alignSelf="center"
      flexDirection={direction}
      marginTop={10}
      style={containerStyle}
      data-cy={`rating-input-${dataCyPrefix}`}
    >
      {title && (
        <Box
          flex={isRowDirection ? '0 0 40%' : '1'}
          paddingRight={isRowDirection ? 12 : 0}
          paddingTop={5}
          textAlign="center"
        >
          <Typography as="span" bold color={colors.neutral[600]}>
            {title}
          </Typography>
        </Box>
      )}
      <Box flex={isRowDirection ? '0 0 60%' : '1'} marginTop={isColumnDirection ? 12 : 0}>
        <Row alignItems="center" flexWrap="nowrap">
          {Array.from({ length: maxValue }, (_, index) => {
            const starNumber = index + 1;
            const isSelected = value >= starNumber;

            return (
              <img
                key={starNumber}
                src={isSelected ? greenStar : grayStar}
                onClick={handleStarClick(starNumber)}
                className="star-size"
                data-cy={`${dataCyPrefix}-star-${starNumber}`}
                style={{
                  marginRight: 26,
                  cursor: 'pointer',
                }}
                alt={`${starNumber} star${starNumber > 1 ? 's' : ''}`}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onChange(starNumber);
                  }
                }}
              />
            );
          })}
        </Row>
        <Box height={22} marginTop={8}>
          <Typography as="span" color={colors.error[500]}>{isError ? errorMessage : ''}</Typography>
        </Box>
      </Box>
    </Box>
  );
};
